import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule, CurrencyPipe, PercentPipe, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  getForecastRange,
  type ForecastHorizon,
  type ForecastPeriod
} from '../../services/forecast-cash-flow.service';
import {
  ForecastPipelineService,
  pipelineStageLabel,
  saleTypeLabel,
  type PipelineForecastData,
  type PipelineSaleRow
} from '../../services/forecast-pipeline.service';

@Component({
  selector: 'app-forecast-pipeline',
  templateUrl: './forecast-pipeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    CurrencyPipe,
    PercentPipe,
    DecimalPipe,
    ChartModule,
    TableModule,
    TagModule,
    TooltipModule
  ],
  styles: [
    `
      :host ::ng-deep .p-chart {
        display: block;
        width: 100%;
        height: 100%;
      }
      :host ::ng-deep .p-chart canvas {
        max-width: 100% !important;
        max-height: 100% !important;
      }
      ::ng-deep .forecast-help-tooltip.p-tooltip,
      ::ng-deep .p-tooltip.forecast-help-tooltip {
        max-width: 22rem;
      }
      ::ng-deep .forecast-help-tooltip .p-tooltip-text,
      ::ng-deep .p-tooltip.forecast-help-tooltip .p-tooltip-text {
        white-space: pre-line;
        line-height: 1.45;
        font-size: 0.75rem;
        text-align: left;
      }
    `
  ]
})
export class ForecastPipelineComponent implements OnInit {
  private pipelineService = inject(ForecastPipelineService);

  readonly help = {
    pipeline:
      '¿Cuánto vale tu embudo abierto?\n\n' +
      'Suma el monto de todas las ventas que aún están en proceso comercial (prospecto → aceptada, más suspendidas).\n\n' +
      'Es el tamaño bruto de tu pipeline, sin ajustar por probabilidad.',

    weighted:
      '¿Cuánto es realista que cierres?\n\n' +
      'Embudo abierto × probabilidad de cada etapa, más lo ya Pagado (100%).\n\n' +
      '• Aceptado 90%\n' +
      '• Contrato/Anexo 70%\n' +
      '• OC 85%\n' +
      '• Facturado 95%\n' +
      '• Factura ingresada 97%\n' +
      '• Pagado 100%\n\n' +
      'Incluye lo ya cobrado; no puede ser menor a Pagado.',

    accepted:
      '¿Cuánto ya está casi cerrado?\n\n' +
      'Ventas en etapa Aceptada (90% de probabilidad).\n\n' +
      'Son las oportunidades más cercanas a convertirse en ingreso.',

    winRate:
      '¿Qué tan bien cierras?\n\n' +
      'Aceptadas ÷ (Aceptadas + Perdidas), en monto.\n\n' +
      'Más alto = mejor conversión del embudo.',

    funnel:
      'El embudo por etapa.\n\n' +
      'Barras = monto bruto por etapa.\n' +
      'Línea = monto ponderado (ajustado por probabilidad).\n\n' +
      'Así ves dónde se concentra el valor y qué es más probable cobrar.',

    composition:
      'Reparto del pipeline abierto por etapa.\n\n' +
      'Te muestra en qué fase del embudo está la mayor parte del dinero.',

    saleType:
      '¿Ventas únicas o suscripciones?\n\n' +
      'Separa el pipeline abierto por tipo de venta.',

    openTable:
      'Lista de oportunidades abiertas.\n\n' +
      'Ordenadas por valor ponderado para priorizar seguimiento de las más prometedoras.',

    lostTable:
      'Ventas marcadas como Perdidas.\n\n' +
      'Sirve para revisar qué se escapó y por qué monto.',

    created:
      'Oportunidades creadas en el periodo seleccionado.\n\n' +
      'Cuenta ventas (abiertas o perdidas) cuya fecha de creación cae en el rango.'
  } as const;

  readonly glossary: { term: string; definition: string }[] = [
    {
      term: 'Pipeline bruto',
      definition:
        'Suma de montos de ventas abiertas en el embudo comercial (Prospecto, Propuesta, Negociación, Aceptada, Suspendida).'
    },
    {
      term: 'Pipeline ponderado',
      definition:
        'Mismo pipeline, multiplicando cada venta por la probabilidad de cierre de su etapa. Es el forecast “realista”.'
    },
    {
      term: 'Probabilidad de cierre',
      definition:
        'Porcentaje estimado de que la venta se gane según la etapa: Prospecto 10%, Propuesta 25%, Negociación 50%, Aceptada 90%, Suspendida 15%, Perdida 0%.'
    },
    {
      term: 'Win rate',
      definition:
        'Tasa de cierre = monto aceptado ÷ (aceptado + perdido). Mide efectividad comercial del embudo.'
    },
    {
      term: 'Prospecto',
      definition: 'Primer contacto / oportunidad temprana. Baja probabilidad de cierre.'
    },
    {
      term: 'Propuesta enviada',
      definition: 'Ya se envió cotización o propuesta al cliente.'
    },
    {
      term: 'Negociación',
      definition: 'Se está discutiendo precio, alcance o condiciones.'
    },
    {
      term: 'Aceptada',
      definition: 'El cliente aceptó. Alta probabilidad de convertirse en ingreso/cobro.'
    },
    {
      term: 'Suspendida',
      definition: 'Oportunidad en pausa. Sigue abierta pero con baja probabilidad.'
    },
    {
      term: 'Perdida',
      definition: 'La oportunidad se cerró sin ganar. Ya no forma parte del pipeline abierto.'
    }
  ];

  data = signal<PipelineForecastData | null>(null);
  period = signal<ForecastPeriod>({ horizon: '6m' });
  customDateFrom = signal('');
  customDateTo = signal('');
  tableTab = signal<'open' | 'lost'>('open');
  glossaryOpen = signal(false);

  readonly horizonOptions: { value: ForecastHorizon; label: string }[] = [
    { value: 'month', label: 'Mes en curso' },
    { value: '3m', label: '3 meses' },
    { value: '6m', label: '6 meses' },
    { value: '12m', label: '12 meses' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly periodLabel = computed(() => {
    const { from, to } = getForecastRange(this.period());
    const fmt = (d: Date) =>
      d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(from)} – ${fmt(to)}`;
  });

  readonly todayLabel = computed(() =>
    new Date().toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  );

  private readonly stageColors: Record<string, string> = {
    ACCEPTED: 'rgba(16, 185, 129, 0.9)',
    CONTRACT_ATTACHMENT: 'rgba(99, 102, 241, 0.9)',
    OC: 'rgba(245, 158, 11, 0.95)',
    INVOICED: 'rgba(14, 165, 233, 0.85)',
    INVOICE_IMPORTED: 'rgba(59, 130, 246, 0.9)',
    PAID: 'rgba(34, 197, 94, 0.95)',
    LOST: 'rgba(244, 63, 94, 0.9)'
  };

  readonly funnelChart = computed(() => {
    const stages = (this.data()?.stages ?? []).filter((s) => s.status !== 'LOST' || s.count > 0);
    return {
      labels: stages.map((s) => s.label),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Monto bruto',
          backgroundColor: stages.map((s) => this.stageColors[s.status] || 'rgba(100,116,139,0.8)'),
          data: stages.map((s) => s.amount),
          borderRadius: 4,
          order: 2
        },
        {
          type: 'line' as const,
          label: 'Ponderado',
          borderColor: 'rgba(15, 23, 42, 0.85)',
          backgroundColor: 'rgba(15, 23, 42, 0.1)',
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 4,
          fill: false,
          data: stages.map((s) => s.weightedAmount),
          order: 1
        }
      ]
    };
  });

  readonly funnelOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            const v = ctx.parsed.y ?? 0;
            return `${ctx.dataset.label}: ${this.formatMxn(v)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(15, 23, 42, 0.06)' },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
          callback: (value: string | number) => this.formatMxn(Number(value))
        }
      }
    }
  };

  readonly compositionChart = computed(() => {
    const stages = (this.data()?.stages ?? []).filter(
      (s) => s.status !== 'LOST' && s.amount > 0
    );
    return {
      labels: stages.map((s) => s.label),
      datasets: [
        {
          data: stages.map((s) => s.amount),
          backgroundColor: stages.map((s) => this.stageColors[s.status] || '#94a3b8'),
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  readonly compositionLegend = computed(() => {
    const stages = (this.data()?.stages ?? []).filter(
      (s) => s.status !== 'LOST' && s.amount > 0
    );
    return stages.map((s) => ({
      label: s.label,
      amount: s.amount,
      color: this.stageColors[s.status] || '#94a3b8'
    }));
  });

  readonly doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    layout: { padding: 0 },
    plugins: { legend: { display: false } }
  };

  readonly tableRows = computed(() => {
    const d = this.data();
    if (!d) return [] as PipelineSaleRow[];
    return this.tableTab() === 'open' ? d.openDeals : d.lostDeals;
  });

  ngOnInit(): void {
    this.load();
  }

  toggleGlossary(): void {
    this.glossaryOpen.update((v) => !v);
  }

  setHorizon(horizon: ForecastHorizon): void {
    if (horizon === 'custom') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      this.customDateFrom.set(`${y}-${m}-${String(now.getDate()).padStart(2, '0')}`);
      const end = new Date(y, now.getMonth() + 6, 0);
      this.customDateTo.set(
        `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
      );
      this.period.set({ horizon: 'custom' });
      return;
    }
    this.period.set({ horizon });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ horizon: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  setTableTab(tab: 'open' | 'lost'): void {
    this.tableTab.set(tab);
  }

  load(): void {
    this.data.update((d) =>
      d
        ? { ...d, loading: true, error: undefined }
        : {
            from: '',
            to: '',
            kpis: {
              pipelineAmount: 0,
              pipelineCount: 0,
              weightedAmount: 0,
              avgProbability: 0,
              acceptedAmount: 0,
              acceptedCount: 0,
              lostAmount: 0,
              lostCount: 0,
              winRate: 0,
              createdInPeriod: 0
            },
            stages: [],
            bySaleType: [],
            openDeals: [],
            lostDeals: [],
            loading: true
          }
    );
    this.pipelineService.load(this.period()).subscribe((d) => this.data.set(d));
  }

  formatMxn(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  formatDay(iso: string | undefined): string {
    if (!iso) return '—';
    if (/^\d{4}-\d{2}-\d{2}/.test(iso)) {
      const [y, m, d] = iso.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  stageLabel = pipelineStageLabel;
  typeLabel = saleTypeLabel;

  stageSeverity(
    status: string
  ): 'secondary' | 'info' | 'warn' | 'success' | 'danger' | 'contrast' {
    switch (status) {
      case 'ACCEPTED':
        return 'success';
      case 'CONTRACT_ATTACHMENT':
        return 'info';
      case 'OC':
        return 'warn';
      case 'INVOICED':
        return 'info';
      case 'INVOICE_IMPORTED':
        return 'contrast';
      case 'PAID':
        return 'success';
      case 'LOST':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
